import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Toolbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { HTMLArtifact } from '../types/api';

enum PreviewMode {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

const ViewerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
}));

const ViewerToolbar = styled(Toolbar)(() => ({
  minHeight: '40px !important',
  borderBottom: '1px solid #2A2A2A',
  background: '#1A1A1A',
  padding: '4px 16px !important',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
}));

const PreviewContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'previewMode'
})<{ previewMode: PreviewMode }>(({ previewMode }) => ({
  height: '100%',
  overflow: 'auto',
  background: '#0D0D0D',
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  scrollbarGutter: 'stable',
  padding: previewMode === PreviewMode.MOBILE ? '20px' : '0',
  '& iframe': {
    width: previewMode === PreviewMode.MOBILE ? '375px' : '100%',
    height: '100%',
    minHeight: previewMode === PreviewMode.MOBILE ? '667px' : '100%',
    maxHeight: 'none',
    border: previewMode === PreviewMode.MOBILE ? '1px solid #444' : 'none',
    borderRadius: previewMode === PreviewMode.MOBILE ? '8px' : '0',
    boxShadow: previewMode === PreviewMode.MOBILE ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
    transition: 'width 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  },
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#1A1A1A',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#666',
    borderRadius: '4px',
    '&:hover': {
      background: '#777',
    },
  },
}));

const StatusChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

interface HTMLViewerProps {
  htmlContent: string;
  artifacts: HTMLArtifact[];
  threadId?: string;
  isLoading?: boolean;
  isStreaming?: boolean;
  onPublishClick?: () => void;
  previewMode: 'desktop' | 'mobile';
}

const HTMLViewer: React.FC<HTMLViewerProps> = ({ 
  htmlContent, 
  artifacts, 
  threadId, 
  isLoading, 
  isStreaming, 
  onPublishClick,
  previewMode: externalPreviewMode
}) => {
  const [previewKey, setPreviewKey] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const latestArtifact = artifacts[artifacts.length - 1];

  // Convert string previewMode to PreviewMode enum
  const previewMode = externalPreviewMode === 'mobile' ? PreviewMode.MOBILE : PreviewMode.DESKTOP;

  useEffect(() => {
    // Throttle iframe refresh when HTML content changes to avoid too many rapid updates
    const timer = setTimeout(() => {
      setPreviewKey(prev => prev + 1);
    }, 300); // 300ms throttle for streaming updates
    
    // Show updating indicator briefly when content changes
    if (htmlContent) {
      setIsUpdating(true);
      const updateTimer = setTimeout(() => setIsUpdating(false), 1500);
      return () => {
        clearTimeout(timer);
        clearTimeout(updateTimer);
      };
    }
    
    return () => clearTimeout(timer);
  }, [htmlContent]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(htmlContent);
      console.log('HTML copied to clipboard');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edited-html.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const createPreviewContent = (html: string) => {
    const viewport = 'width=device-width, initial-scale=1.0';
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="${viewport}">
    <title>HTML Preview</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            line-height: 1.6;
            color: #333;
            background: #fff;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }
        * {
            box-sizing: border-box;
        }
        .container, [class*="container"] {
            max-width: 100% !important;
            width: 100% !important;
        }
        
        /* Styles for the heads-up message */
        .preview-message {
            position: fixed;
            top: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: linear-gradient(135deg, #F05502 0%, #e04a02 100%);
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(240, 85, 2, 0.3);
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: 500;
            z-index: 9999;
            max-width: 90%;
            text-align: center;
            opacity: 0;
            transition: opacity 0.3s ease-in-out;
            pointer-events: none;
        }
        
        .preview-message.show {
            opacity: 1;
        }
        
        .preview-message.hide {
            opacity: 0;
        }
    </style>
</head>
<body>
    <div id="preview-message" class="preview-message">
        🙌 Heads-up! Buttons are disabled in Preview. They'll work on your published page.
    </div>
    
    ${html}
    
    <script>
        (function() {
            // List of interactive elements to intercept
            const INTERACTIVE_SELECTORS = [
                'a[href]',
                'button',
                'input[type="button"]',
                'input[type="submit"]',
                'input[type="reset"]',
                'input[type="checkbox"]',
                'input[type="radio"]',
                'select',
                'textarea',
                'input[type="text"]',
                'input[type="email"]',
                'input[type="password"]',
                'input[type="number"]',
                'input[type="tel"]',
                'input[type="url"]',
                'input[type="search"]',
                'input[type="file"]',
                'input[type="date"]',
                'input[type="time"]',
                'input[type="datetime-local"]',
                'input[type="month"]',
                'input[type="week"]',
                'input[type="color"]',
                'input[type="range"]',
                '[onclick]',
                '[role="button"]',
                '[tabindex="0"]'
            ];
            
            let messageTimeout;
            
            function showMessage() {
                const messageEl = document.getElementById('preview-message');
                if (messageEl) {
                    // Clear any existing timeout
                    if (messageTimeout) {
                        clearTimeout(messageTimeout);
                    }
                    
                    // Show message
                    messageEl.classList.remove('hide');
                    messageEl.classList.add('show');
                    
                    // Hide after 3 seconds
                    messageTimeout = setTimeout(() => {
                        messageEl.classList.remove('show');
                        messageEl.classList.add('hide');
                    }, 3000);
                }
            }
            
            function isInteractiveElement(element) {
                // Check if element matches any of our interactive selectors
                for (const selector of INTERACTIVE_SELECTORS) {
                    if (element.matches && element.matches(selector)) {
                        return true;
                    }
                }
                
                // Check if any parent element is interactive (for nested elements)
                let parent = element.parentElement;
                while (parent && parent !== document.body) {
                    for (const selector of INTERACTIVE_SELECTORS) {
                        if (parent.matches && parent.matches(selector)) {
                            return true;
                        }
                    }
                    parent = parent.parentElement;
                }
                
                return false;
            }
            
            // Add click event listener to intercept clicks on interactive elements
            document.addEventListener('click', function(event) {
                const target = event.target;
                
                if (isInteractiveElement(target)) {
                    // Prevent default behavior
                    event.preventDefault();
                    event.stopPropagation();
                    
                    // Show the heads-up message
                    showMessage();
                    
                    return false;
                }
            }, true); // Use capturing phase to ensure we catch the event first
            
            // Also intercept form submissions
            document.addEventListener('submit', function(event) {
                event.preventDefault();
                event.stopPropagation();
                showMessage();
                return false;
            }, true);
            
            // Intercept focus events on form elements to show message
            document.addEventListener('focus', function(event) {
                const target = event.target;
                if (target.matches && (
                    target.matches('input[type="text"]') ||
                    target.matches('input[type="email"]') ||
                    target.matches('input[type="password"]') ||
                    target.matches('input[type="number"]') ||
                    target.matches('input[type="tel"]') ||
                    target.matches('input[type="url"]') ||
                    target.matches('input[type="search"]') ||
                    target.matches('textarea') ||
                    target.matches('select')
                )) {
                    showMessage();
                }
            }, true);
            
            // Prevent keyboard navigation that might trigger actions
            document.addEventListener('keydown', function(event) {
                const target = event.target;
                
                // Intercept Enter key on buttons and links
                if ((event.key === 'Enter' || event.key === ' ') && isInteractiveElement(target)) {
                    event.preventDefault();
                    event.stopPropagation();
                    showMessage();
                    return false;
                }
            }, true);
        })();
    </script>
</body>
</html>`;
  };

  const handlePublishClick = () => {
    if (onPublishClick) {
      onPublishClick();
    }
  };

  return (
    <ViewerContainer>
      <ViewerToolbar>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Removed desktop/mobile toggle as it's now in the main header */}
        </Box>

        <StatusChips>
          {(isLoading || isUpdating) && (
            <Chip
              label={isLoading ? "Processing..." : "Updated!"}
              color={isLoading ? "info" : "success"}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '11px',
                animation: isLoading ? 'pulse 1.5s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { opacity: 0.6 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.6 },
                },
              }}
            />
          )}
          
          {latestArtifact && (
            <>
              <Chip
                label={latestArtifact.isValid ? 'Valid HTML' : 'Invalid HTML'}
                color={latestArtifact.isValid ? 'success' : 'error'}
                size="small"
                variant="outlined"
                sx={{ fontSize: '11px' }}
              />
              {latestArtifact.warnings.length > 0 && (
                <Chip
                  label={`${latestArtifact.warnings.length} Warning${latestArtifact.warnings.length !== 1 ? 's' : ''}`}
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '11px' }}
                />
              )}
            </>
          )}
          
          <Tooltip title="Copy HTML">
            <IconButton size="small" onClick={handleCopyToClipboard} sx={{ color: '#999' }}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Download HTML">
            <IconButton size="small" onClick={handleDownload} sx={{ color: '#999' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </StatusChips>
      </ViewerToolbar>

      <ContentContainer>
        {latestArtifact && latestArtifact.errors.length > 0 && (
          <Alert severity="error" sx={{ m: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              HTML Validation Errors:
            </Typography>
            {latestArtifact.errors.map((error, index) => (
              <Typography key={index} variant="body2">
                • {error}
              </Typography>
            ))}
          </Alert>
        )}

        {latestArtifact && latestArtifact.warnings.length > 0 && (
          <Alert severity="warning" sx={{ m: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              HTML Warnings:
            </Typography>
            {latestArtifact.warnings.map((warning, index) => (
              <Typography key={index} variant="body2">
                • {warning}
              </Typography>
            ))}
          </Alert>
        )}

        <PreviewContainer previewMode={previewMode}>
          {htmlContent.trim() ? (
            <iframe
              key={previewKey}
              srcDoc={createPreviewContent(htmlContent)}
              title="HTML Preview"
              sandbox="allow-scripts allow-same-origin"
              loading="lazy"
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#666',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <VisibilityIcon sx={{ fontSize: 48, opacity: 0.5 }} />
              <Typography variant="h6" sx={{ color: '#999' }}>No HTML to preview</Typography>
              <Typography variant="body2" textAlign="center" sx={{ color: '#666' }}>
                Start a conversation to generate HTML content
              </Typography>
            </Box>
          )}
        </PreviewContainer>
      </ContentContainer>
    </ViewerContainer>
  );
};

export default HTMLViewer; 